import * as _ from "lodash";
var fp = require('lodash/fp');

interface IMap<T> {
    [key: string]: T;
}
type Pipe<T, U> = (collection: T[]) => U[];
type PipeUniform<T> = Pipe<T, T>;
type Filter<T> = (item: T) => boolean;
type Mapping<T, U> = (item: T) => U;
type Iteratee<T> = (string|Mapping<T, any>)[];

/**
 * Gets the size of collection by returning its length for array-like values or the number of own enumerable
 * properties for objects.
 *
 * @param collection The collection to inspect.
 * @return Returns the size of collection.
 */
function size(collection: object | string | null | undefined): number {
    return _.size(collection);
}

/**
 * Iterates over elements of collection, returning an array of all elements predicate returns truthy for. The
 * predicate is invoked with three arguments: (value, index|key, collection).
 *
 * @param collection The collection to iterate over.
 * @param predicate The function invoked per iteration.
 * @return Returns the new filtered array.
 */
function filter(
    collection: string | null | undefined,
    predicate?: _.StringIterator<boolean>
): string[];

/**
 * @see _.filter
 */
function filter<T, S extends T>(
    collection: _.List<T> | null | undefined,
    predicate: _.ListIteratorTypeGuard<T, S>
): S[];

/**
 * @see _.filter
 */
function filter<T>(
    collection: _.List<T> | null | undefined,
    predicate?: _.ListIterateeCustom<T, boolean>
): T[];

/**
 * @see _.filter
 */
function filter<T extends object, S extends T[keyof T]>(
    collection: T | null | undefined,
    predicate: _.ObjectIteratorTypeGuard<T, S>
): S[];

/**
 * @see _.filter
 */
function filter<T extends object>(
    collection: T | null | undefined,
    predicate?: _.ObjectIterateeCustom<T, boolean>
): Array<T[keyof T]>;
function filter(collection, predicate?) {
    return _.filter(collection, predicate);
}

function fpFilter<T>(itemMatch: IMap<any>): PipeUniform<T>;
function fpFilter<T>(predicate: Filter<T>): PipeUniform<T>;
function fpFilter(...args) {
    return fp.filter(...args);
}

/**
 * Creates an array of values by running each element in collection through iteratee. The iteratee is
 * invoked with three arguments: (value, index|key, collection).
 *
 * Many lodash methods are guarded to work as iteratees for methods like _.every, _.filter, _.map, _.mapValues,
 * _.reject, and _.some.
 *
 * The guarded methods are:
 * ary, callback, chunk, clone, create, curry, curryRight, drop, dropRight, every, fill, flatten, invert, max,
 * min, parseInt, slice, sortBy, take, takeRight, template, trim, trimLeft, trimRight, trunc, random, range,
 * sample, some, sum, uniq, and words
 *
 * @param collection The collection to iterate over.
 * @param iteratee The function invoked per iteration.
 * @return Returns the new mapped array.
 */
function map<T, TResult>(collection: T[] | null | undefined,
    iteratee: _.ArrayIterator<T, TResult>): TResult[] {
    return _.map(collection, iteratee);
}

function fpMap<T, U>(mapper: Mapping<T, U>): Pipe<T, U>;
function fpMap(...args) {
    return fp.map(...args);
}

// Note: Array flat and flatMap are available in es2019. They can probably be adopted instead in Phase II
export type Many<T> = T | ReadonlyArray<T>;
export type List<T> = ArrayLike<T>;
export type ListIterator<T, TResult> = (value: T, index: number, collection: Array<T>) => TResult;
export interface Dictionary<T> {
    [index: string]: T;
}
export interface NumericDictionary<T> {
    [index: number]: T;
}
/**
 * Creates an array of flattened values by running each element in collection through iteratee
 * and concating its result to the other mapped values. The iteratee is invoked with three arguments:
 * (value, index|key, collection).
 *
 * @param collection The collection to iterate over.
 * @param iteratee The function invoked per iteration.
 * @return Returns the new flattened array.
 */
function flatMap<T>(
    collection: List<Many<T>> | Dictionary<Many<T>> | NumericDictionary<Many<T>> | null | undefined
): T[];
function flatMap<T, TResult>(
    collection: List<T> | null | undefined,
    iteratee: ListIterator<T, Many<TResult>>
): TResult[];
function flatMap(collection, iteratee?) {
    if (iteratee !== undefined) {
        return _.flatMap(collection, iteratee);
    } else {
        return _.flatMap(collection);
    }
}

/**
 * Creates an array of shuffled values, using a version of the Fisher-Yates shuffle.
 *
 * @param collection The collection to shuffle.
 * @return Returns the new shuffled array.
 */
function shuffle<T>(collection: Array<T> | null | undefined): T[] {
    return _.shuffle(collection);
}

function fpShuffle<T>(collection: T[]): T[];
function fpShuffle(...args) {
    return fp.shuffle(...args);
}

/**
 * Iterates over elements of collection, returning the first element predicate returns truthy for.
 * The predicate is invoked with three arguments: (value, index|key, collection).
 *
 * @param collection The collection to search.
 * @param predicate The function invoked per iteration.
 * @param fromIndex The index to search from.
 * @return Returns the matched element, else undefined.
 */
function find<T, S extends T>(
    collection: _.List<T> | null | undefined,
    predicate: _.ListIteratorTypeGuard<T, S>,
    fromIndex?: number
): S | undefined;

/**
 * @see _.find
 */
function find<T>(
    collection: _.List<T> | null | undefined,
    predicate?: _.ListIterateeCustom<T, boolean>,
    fromIndex?: number
): T | undefined;

/**
 * @see _.find
 */
function find<T extends object, S extends T[keyof T]>(
    collection: T | null | undefined,
    predicate: _.ObjectIteratorTypeGuard<T, S>,
    fromIndex?: number
): S | undefined;

/**
 * @see _.find
 */
function find<T extends object>(
    collection: T | null | undefined,
    predicate?: _.ObjectIterateeCustom<T, boolean>,
    fromIndex?: number
): T[keyof T] | undefined;

function find(collection, predicate, fromIndex?) {
    if (fromIndex !== undefined) {
        return _.find(collection, predicate, fromIndex);
    } else {
        return _.find(collection, predicate);
    }
}

/**
 * Creates an object composed of keys generated from the results of running each element of collection through
 * iteratee. The corresponding value of each key is the last element responsible for generating the key. The
 * iteratee function is invoked with one argument: (value).
 *
 * @param collection The collection to iterate over.
 * @param iteratee The function invoked per iteration.
 * @return Returns the composed aggregate object.
 */
function keyBy<T>(
    collection: _.List<T> | null | undefined,
    iteratee?: _.ValueIterateeCustom<T, _.PropertyName>
): _.Dictionary<T>;
function keyBy<T extends object>(
    collection: T | null | undefined,
    iteratee?: _.ValueIterateeCustom<T[keyof T], _.PropertyName>
): _.Dictionary<T[keyof T]>;
function keyBy(collection, iteratee?) {
    return _.keyBy(collection, iteratee);
}

/**
 * Iterates over elements of collection invoking iteratee for each element. The iteratee is invoked with three arguments:
 * (value, index|key, collection). Iteratee functions may exit iteration early by explicitly returning false.
 *
 * Note: As with other "Collections" methods, objects with a "length" property are iterated like arrays. To
 * avoid this behavior _.forIn or _.forOwn may be used for object iteration.
 *
 * @alias _.each
 *
 * @param collection The collection to iterate over.
 * @param iteratee The function invoked per iteration.
 */
function forEach<T>(
    collection: T[],
    iteratee?: _.ArrayIterator<T, any>
): T[];
/**
 * @see _.forEach
 */
function forEach<T>(
    collection: List<T>,
    iteratee?: ListIterator<T, any>
): List<T>;
/**
 * @see _.forEach
 */
function forEach<T extends object>(
    collection: T,
    iteratee?: _.ObjectIterator<T, any>
): T;
/**
 * @see _.forEach
 */
function forEach<T, TArray extends T[] | null | undefined>(
    collection: TArray & (T[] | null | undefined),
    iteratee?: _.ArrayIterator<T, any>
): TArray;
/**
 * @see _.forEach
 */
function forEach<TString extends string | null | undefined>(
    collection: TString,
    iteratee?: _.StringIterator<any>
): TString;
/**
 * @see _.forEach
 */
function forEach<T, TList extends List<T> | null | undefined>(
    collection: TList & (List<T> | null | undefined),
    iteratee?: ListIterator<T, any>
): TList;
/**
 * @see _.forEach
 */
function forEach<T extends object>(
    collection: T | null | undefined,
    iteratee?: _.ObjectIterator<T, any>
): T | null | undefined;
function forEach(collection, iteratee?) {
    return _.forEach(collection, iteratee);
}

export type PropertyName = string | number | symbol;
export type PartialDeep<T> = {
    [P in keyof T]?: PartialDeep<T[P]>;
};
export type NotVoid = {} | null | undefined;
export type ObjectIterator<TObject, TResult> = (value: TObject[keyof TObject], key: string, collection: TObject) => TResult;
export type IterateeShorthand<T> = PropertyName | [PropertyName, any] | PartialDeep<T>;
export type ObjectIteratee<TObject> = ObjectIterator<TObject, NotVoid> | IterateeShorthand<TObject[keyof TObject]>;
export type ListIteratee<T> = ListIterator<T, NotVoid> | IterateeShorthand<T>;
/**
 * Creates an array of elements, sorted in ascending order by the results of
 * running each element in a collection through each iteratee. This method
 * performs a stable sort, that is, it preserves the original sort order of
 * equal elements. The iteratees are invoked with one argument: (value).
 *
 * @category Collection
 * @param collection The collection to iterate over.
 * @param [iteratees=[_.identity]]
 *  The iteratees to sort by, specified individually or in arrays.
 * @returns Returns the new sorted array.
 * @example
 *
 * var users = [
 *   { 'user': 'fred',   'age': 48 },
 *   { 'user': 'barney', 'age': 36 },
 *   { 'user': 'fred',   'age': 42 },
 *   { 'user': 'barney', 'age': 34 }
 * ];
 *
 * _.sortBy(users, function(o) { return o.user; });
 * // => objects for [['barney', 36], ['barney', 34], ['fred', 48], ['fred', 42]]
 *
 * _.sortBy(users, ['user', 'age']);
 * // => objects for [['barney', 34], ['barney', 36], ['fred', 42], ['fred', 48]]
 *
 * _.sortBy(users, 'user', function(o) {
 *   return Math.floor(o.age / 10);
 * });
 * // => objects for [['barney', 36], ['barney', 34], ['fred', 48], ['fred', 42]]
 */
function sortBy<T>(
    collection: List<T> | null | undefined,
    ...iteratees: Array<Many<ListIteratee<T>>>
): T[];
/**
 * @see _.sortBy
 */
function sortBy<T extends object>(
    collection: T | null | undefined,
    ...iteratees: Array<Many<ObjectIteratee<T>>>
): Array<T[keyof T]>;
function sortBy(collection, ...iteratees) {
    return _.sortBy(collection, ...iteratees);
}

/**
 * This method is like `_.sortBy` except that it allows specifying the sort
 * orders of the iteratees to sort by. If `orders` is unspecified, all values
 * are sorted in ascending order. Otherwise, specify an order of "desc" for
 * descending or "asc" for ascending sort order of corresponding values.
 *
 * @category Collection
 * @param collection The collection to iterate over.
 * @param [iteratees=[_.identity]] The iteratees to sort by.
 * @param [orders] The sort orders of `iteratees`.
 * @param- {Object} [guard] Enables use as an iteratee for functions like `_.reduce`.
 * @returns Returns the new sorted array.
 * @example
 *
 * var users = [
 *   { 'user': 'fred',   'age': 48 },
 *   { 'user': 'barney', 'age': 34 },
 *   { 'user': 'fred',   'age': 42 },
 *   { 'user': 'barney', 'age': 36 }
 * ];
 *
 * // sort by `user` in ascending order and by `age` in descending order
 * _.orderBy(users, ['user', 'age'], ['asc', 'desc']);
 * // => objects for [['barney', 36], ['barney', 34], ['fred', 48], ['fred', 42]]
 */
function orderBy<T>(
    collection: List<T> | null | undefined,
    iteratees?: Many<ListIterator<T, NotVoid>>,
    orders?: Many<boolean|"asc"|"desc">
): T[];

/**
 * @see _.orderBy
 */
function orderBy<T>(
    collection: List<T> | null | undefined,
    iteratees?: Many<ListIteratee<T>>,
    orders?: Many<boolean|"asc"|"desc">
): T[];

/**
 * @see _.orderBy
 */
function orderBy<T extends object>(
    collection: T | null | undefined,
    iteratees?: Many<ObjectIterator<T, NotVoid>>,
    orders?: Many<boolean|"asc"|"desc">
): Array<T[keyof T]>;

/**
 * @see _.orderBy
 */
function orderBy<T extends object>(
    collection: T | null | undefined,
    iteratees?: Many<ObjectIteratee<T>>,
    orders?: Many<boolean|"asc"|"desc">
): Array<T[keyof T]>;

function orderBy(collection, iteratees?, orders?) {
    return _.orderBy(collection, iteratees, orders)
}

function fpOrderBy<T>(iteratees: Iteratee<T>, orders: string[]): PipeUniform<T>;
function fpOrderBy(...args) {
    return fp.orderBy(...args);
}

/**
 * Checks if target is in collection using SameValueZero for equality comparisons. If fromIndex is negative,
 * it’s used as the offset from the end of collection.
 *
 * @param collection The collection to search.
 * @param target The value to search for.
 * @param fromIndex The index to search from.
 * @return True if the target element is found, else false.
 */
function includes<T>(collection: Dictionary<T> | NumericDictionary<T> | null | undefined, target: T, fromIndex?: number): boolean {
    return _.includes(collection, target, fromIndex)
};

/**
 * Checks if predicate returns truthy for any element of collection. Iteration is stopped once predicate
 * returns truthy. The predicate is invoked with three arguments: (value, index|key, collection).
 *
 * @param collection The collection to iterate over.
 * @param predicate The function invoked per iteration.
 * @return Returns true if any element passes the predicate check, else false.
 */
function some<T>(collection: List<T> | null | undefined, predicate?: _.ListIterateeCustom<T, boolean>): boolean;
/**
 * @see _.some
 */
function some<T extends object>(collection: T | null | undefined, predicate?: _.ObjectIterateeCustom<T, boolean>): boolean;

function some(collection, predicate?) {
    return _.some(collection, predicate)
}

/**
 * @see _.forEach
 */
function each<T>(
    collection: T[],
    iteratee?: ListIterator<T, any>
): T[];

/**
 * @see _.forEach
 */
function each<T>(
    collection: List<T>,
    iteratee?: ListIterator<T, any>
): List<T>;

/**
 * @see _.forEach
 */
function each<T>(
    collection: Dictionary<T>,
    iteratee?: _.DictionaryIterator<T, any>
): Dictionary<T>;

/**
 * @see _.forEach
 */
function each<T extends {}>(
    collection: T,
    iteratee?: ObjectIterator<any, any>
): T;

/**
 * @see _.forEach
 */
function each<T extends {}, TValue>(
    collection: T,
    iteratee?: ObjectIterator<TValue, any>
): T;

function each(collection, iteratee?) {
    return _.each(collection, iteratee)
}

/**
 * wraps the value to enable implicit chaining using chain
 * @param value The value to wrap
 * @return Returns the new lodash wrapper instance
 * @example
 * var users = [
 *  { 'user': 'barney',  'age': 36 },
 * { 'user': 'fred',    'age': 40 },
 * { 'user': 'pebbles', 'age': 1 }
 * ];
 * var youngest = _
 * .chain(users)
 * .sortBy('age')
 * .map(function(o) {
 * return o.user + ' is ' + o.age;
 * })
 * .head()
 * .value();
 * // => 'pebbles is 1'
 */
function chain(value: any): any {
    return _.chain(value);
}

export const CollectionUtil = {
    size, filter, fpFilter, map, fpMap, flatMap, shuffle, fpShuffle, find, keyBy, forEach, sortBy, orderBy, fpOrderBy, includes, some, each, chain
}

export default CollectionUtil;